//libs
import React, { memo, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty';

//selectors
import { getAppliedFilters, getFilters } from 'shared/store/listingV2/selectors';

//hooks
import { useFilters } from 'shared/store/listingV2/hooks';

//helpers
import { transformFilters } from './helpers';

//styles
import { Heading, Wrapper } from './styles';

//components
import { PopUpView, ListView, TreeView, PriceFilter } from 'shared/components/Filter';
import ResetButton from 'shared/components/Filter/components/ResetButton';
import StickyBox from 'react-sticky-box';

//constants
import { VIEW_TYPES, TEXTS, FILTER_TEXT_HEIGHT } from './constants';

//analytics
import {
  exitFilterDrawer,
  onFiltersApplied,
} from 'shared/layouts/Listing/components/Analytics/helpers';

interface IFilterProps {
  stickyTopValue: number;
}

const Filter = ({ stickyTopValue }: IFilterProps) => {
  const _filters = useSelector(getFilters);
  const appliedValues = useSelector(getAppliedFilters);
  const { updateAppliedFilter } = useFilters();
  const filters = useMemo(() => transformFilters(_filters), [_filters]);

  const [openFilter, setOpenFilter] = useState<string | null>(null);

  useEffect(() => {
    const appliedFilters = Object.assign({}, appliedValues);
    delete appliedFilters.sort;

    if (isEmpty(appliedFilters)) setOpenFilter(null);
    onFiltersApplied(appliedValues);
  }, [appliedValues]);

  const getComponent = (filter: Listing.IFilter) => {
    switch (filter.filterView) {
      case VIEW_TYPES.LIST:
        return ListView;
      case VIEW_TYPES.TREE:
        return TreeView;
      case VIEW_TYPES.BRAND_GRID:
      case VIEW_TYPES.OTHER_GRID:
        return PopUpView;
      case VIEW_TYPES.SLIDER:
        return PriceFilter;
      default:
        return React.Fragment;
    }
  };

  const renderComponent = (filter: Listing.IFilter) => {
    const checkedFilter = appliedValues[filter.key];
    const Component = getComponent(filter);

    const getCheckedFilters = () => {
      if (filter.filterView === VIEW_TYPES.SLIDER) {
        if (checkedFilter) {
          const arr = checkedFilter.split('-');
          return { min: Number(arr[0]), max: Number(arr[1]) };
        } else return {};
      }
      if (checkedFilter) return checkedFilter.split('_').filter(Boolean);
      return [];
    };

    const onClick = () => {
      if (openFilter === filter.key) {
        if (
          filter.filterView === VIEW_TYPES.TREE ||
          filter.filterView === VIEW_TYPES.OTHER_GRID ||
          filter.filterView === VIEW_TYPES.BRAND_GRID
        ) {
          exitFilterDrawer();
        }
        setOpenFilter(null);
        return;
      }
      setOpenFilter(filter.key);
    };

    const onApplyClick = (values: string) => {
      const appliedFiltersValues = { ...appliedValues };
      appliedFiltersValues[filter.key] = values;
      updateAppliedFilter(appliedFiltersValues);
      //analytics
      onFiltersApplied(appliedFiltersValues, true);
    };

    return (
      <Component
        filter={filter}
        onApplyClick={onApplyClick}
        checkedFilters={getCheckedFilters()}
        showAlphabetHeading={filter.filterView === VIEW_TYPES.BRAND_GRID}
        isCollapsedView={openFilter !== filter.key}
        onClick={onClick}
        key={filter.key}
      />
    );
  };
  return (
    <StickyBox offsetTop={stickyTopValue + FILTER_TEXT_HEIGHT} offsetBottom={0}>
      <Wrapper>
        <Heading>{TEXTS.FILTERS}</Heading>
        <ResetButton />
      </Wrapper>
      {filters.map(renderComponent)}
    </StickyBox>
  );
};

export default memo(Filter);
